<template>
  <div class="block-quiz-container pa-3" style="position: relative;">

    <div v-if="isLoading" class="text-center my-8">
      <v-progress-circular indeterminate color="primary" :size="60" :width="6"></v-progress-circular>
      <p class="mt-4 text-caption grey--text">Cargando cuestionario del bloque...</p>
    </div>

    <div v-else-if="error" class="text-center my-8">
      <v-alert type="error" dense outlined>
        {{ error }}
      </v-alert>
      <v-btn small color="primary" class="mt-3" @click="fetchQuiz">
        Reintentar
      </v-btn>
    </div>

    <div v-else-if="!hasQuizContent && !quizResultData" class="text-center my-8">
      <v-alert type="info" dense outlined>
        No se encontró un cuestionario para este bloque.
      </v-alert>
      <v-btn small color="primary" class="mt-3" @click="generateQuiz" :loading="isGenerating">
        Generar cuestionario para este bloque
      </v-btn>
    </div>

    <div v-else style="position: relative; flex: 1; margin: 0 auto; min-width: 100%;" class="d-flex flex-column"
    :style="$vuetify.breakpoint.mdAndUp ? 'max-width: 800px;' : 'max-width: 100%;'"
    >
      <div class="mb-5 text-center" style="border-bottom: 1px solid #e0e0e0; padding-bottom: 10px;">
        <p class="text-overline mb-0 grey--text font-rubik">Cuestionario</p>
        <span class="font-weight-bold font-rubik" style="font-size: 1.2rem;">{{ blockName }}</span>
      </div>
       <!-- Utilitzem el component existent per mostrar preguntes -->
       <quiz-single-question
         :quiz="quiz" 
         :settings="blockSettings" 
         :show-results-param="showResults" 
         :quiz-result-data="quizResultData"
         :is-block-mode="false" 
         :document-id="documentId"
         :block-id="blockId"
         :navWidth="navWidth"
         :block-unique-id="blockUniqueId"
         @quiz-completed="handleQuizCompletion" 
         @quiz-retry="restartQuiz"
         @navigate-next="handleNavigateNext"
         @navigate-previous="handleNavigatePrevious"
         ref="quizComponent"
       ></quiz-single-question>
    </div>


  </div>
</template>

<script>
// Importem el component que mostra les preguntes
import QuizSingleQuestion from '@/components/study/QuizSingleQuestion.vue';

export default {
  name: 'BlockQuizRunner',
  components: {
    QuizSingleQuestion
  },
  props: {
    documentId: {
      type: [String, Number],
      required: true
    },
    blockId: { // ID numèric del bloc a la BD
      type: [String, Number],
      required: true
    },
    blockUniqueId: { // ID únic de text del bloc
      type: String,
      required: true
    },
     blockName: { // Nom del bloc per a títols/referències
       type: String,
       default: 'Bloque'
     },
     navWidth: {
      type: Number,
      default: 0
     }
  },
  data() {
    return {
      quiz: { // Estructura similar a la de QuizView
        multiple_choice: [],
        true_false: [],
        fill_in_the_blank: [],
        short_answer: [],
        // Podrien afegir-se més tipus si són rellevants per bloc
      },
      isLoading: false, 
      isGenerating: false,
      error: null,
      blockSettings: { // Configuració simplificada per bloc
        shuffleQuestions: true,
        showExplanations: true,
        // Podríem afegir dificultat si fos necessari
      },
      showResults: false, // Per indicar si mostrar resultats
      quizResultData: null // Per guardar dades del resultat si volem mostrar-les
    };
  },
  computed: {
    hasQuizContent() {
      // Comprovació similar a QuizView
      return Object.values(this.quiz).some(type => Array.isArray(type) && type.length > 0);
    },
    isPremium() {
			return this.$root.currentUser && this.$root.currentUser.premium;
    }
  },
  watch: {
    // Recarregar el qüestionari si canvia l'ID del bloc
    blockId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchQuiz();
      }
    }
  },
  created() {
    this.fetchQuiz(); // Carregar el qüestionari en crear el component
  },
  methods: {
    async fetchQuiz() {
      if (!this.blockId) return;
      this.isLoading = true;
      this.error = null;
      this.quiz = {}; // Netejar quiz anterior
      this.showResults = false;
      this.quizResultData = null;

      try {
        // --- NOU ENDPOINT REQUERIT --- 
        const response = await this.axios.get(`/blocks/${this.blockId}/quiz`, {
          headers: { Authorization: this.$root.token.value }
        });

        if (response.data.success) {
          this.quiz = response.data.quiz || {}; 
           // Si no hi ha contingut, no cal fer res més aquí
        } else {
           // Si l'error és que no existeix (404 per exemple), no el mostrem com a error,
           // simplement es mostrarà el botó de generar.
           if (response.status !== 404) { 
               this.error = response.data.error || 'No se pudo cargar el cuestionario del bloque';
           }
        }
      } catch (err) {
          // Podem gestionar l'error 404 aquí també
          if (err.response && err.response.status === 404) {
              console.log('No quiz found for this block, generation needed.');
          } else {
              console.error('Error al cargar el cuestionario del bloque:', err);
              this.error = 'Error al cargar el cuestionario del bloque.';
          }
      } finally {
        this.isLoading = false;
      }
    },

    async generateQuiz() {
      if (!this.blockId) return;
      this.isGenerating = true;
      this.error = null;

      try {
        // --- NOU ENDPOINT REQUERIT --- 
        const response = await this.axios.post(`/blocks/${this.blockId}/generate-quiz`, {
           language: 'auto' // Podem afegir paràmetres si cal
        }, {
          headers: { Authorization: this.$root.token.value }
        });

        if (response.data.success) {
          this.quiz = response.data.quiz || {};
          this.showResults = false; // Assegurar que no mostrem resultats antics
          this.quizResultData = null;
          this.$toast.success('Cuestionario generado para este bloque');
           // Reset QuizSingleQuestion state if it exists
           if (this.$refs.quizComponent) {
               this.$refs.quizComponent.resetQuiz();
           }
        } else {
          this.error = response.data.error || 'No se pudo generar el cuestionario para el bloque';
           this.$toast.error(this.error);
        }
      } catch (err) { {
        console.error('Error generando cuestionario para el bloque:', err);
        this.error = 'Error al generar el cuestionario para el bloque.';
         this.$toast.error(this.error);
      }} finally {
        this.isGenerating = false;
      }
    },

    // Mètode cridat per QuizSingleQuestion quan el qüestionari es completa
    handleQuizCompletion(resultData) {
        console.log('Quiz del bloque completado:', resultData);
        this.quizResultData = resultData; // Guardem les dades del resultat
        this.showResults = true;
        
        // Marcar como completado este módulo
        this.markAsCompleted(resultData.score);
        
        // Aquí podríem, opcionalment, guardar els resultats al backend
        // this.saveQuizResults(resultData);
    },

    // Método para marcar como completado este cuestionario
    markAsCompleted(score = null) {
        // Notificar al componente padre que se ha completado
        this.$emit('section-completed', { type: 'quiz', score: score });
        
        // También actualizar directamente mediante API si es necesario
        try {
            this.axios.post('/documents/update-progress', {
                documentId: this.documentId,
                blockId: this.blockId,
                contentType: 'quiz',
                completed: true
            }, {
                headers: { Authorization: this.$root.token.value }
            });
            console.log(`Progreso del cuestionario actualizado para bloque ${this.blockId}`);
        } catch (error) {
            console.error('Error al actualizar el progreso:', error);
        }
    },

    // Opcional: Guardar resultats al backend
    async saveQuizResults(resultData) {
        if (!this.blockId || !resultData) return;
        try {
            // --- NOU ENDPOINT REQUERIT (opcional) --- 
             await this.axios.post(`/blocks/${this.blockId}/quiz-results`, {
                 score: resultData.score,
                 totalQuestions: resultData.totalQuestions,
                 answers: resultData.answers // Detalls de les respostes
                 // Altres dades rellevants
             }, {
                 headers: { Authorization: this.$root.token.value }
             });
             console.log('Resultados del cuestionario del bloque guardados.');
         } catch (err) {
             console.error('Error al guardar los resultados del cuestionario del bloque:', err);
             // No és crític si falla, només log
         }
    },

    // Reiniciar el qüestionari per tornar-lo a fer
    restartQuiz() {
        this.showResults = false;
        this.quizResultData = null;
        if (this.$refs.quizComponent) {
            this.$refs.quizComponent.resetQuiz(); // Cridem el mètode del fill
        }
    },

    // Método para propagar la navegación al siguiente bloque
    handleNavigateNext() {
      console.log("handleNavigateNext")
        this.$emit('request-navigation-next');
        
    },

    // Método para propagar la navegación al bloque anterior
    handleNavigatePrevious() {
        this.$emit('request-navigation-previous');
    }
  }
};
</script>

<style scoped>

.block-quiz-container {
    /* Allow flex container to manage height */
    display: flex; 
    flex-direction: column;
    flex: 1; 
    height: calc(100vh - 60px);
    overflow-y: auto;
    width: 100%;
}

/* Ajustar l'estil del component fill si és necessari */
/* Per exemple, si QuizSingleQuestion té marges que volem eliminar aquí */
.block-quiz-container >>> .quiz-single-question-wrapper {
    padding: 0; /* Eliminar padding si el fill en té */
    flex: 1; /* Allow child component to fill available space */
    display: flex;
    flex-direction: column;
}
</style> 